import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Notification from "../../components/Notification.js";
import { API_URL } from "../../config.js";
import { allowedToView } from "../../utils/PermissionUtils.js";
import { formatTimestamp, formatToDayMonthYear, formatCurrency } from "../../utils/TextUtils.js";

function MilkSaleDetails() {
    const [sale, setSale] = useState(null);
    const [aggregationSummary, setAggregationSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const { id } = useParams();
    const navigate = useNavigate();
    const token = localStorage.getItem("access_token");
    const userRole = localStorage.getItem("userRole");

    const canEdit = allowedToView(userRole, ['superadmin', 'aggregator']);

    useEffect(() => {
        const fetchSaleDetails = async () => {
            try {
                setLoading(true);
                const response = await fetch(`${API_URL}/milk-sales/${id}`, {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setSale(data.data);
                    setAggregationSummary(data.aggregation_summary);
                } else if (response.status === 401) {
                    navigate("/logout");
                } else if (response.status === 404) {
                    setNotification({ type: "danger", message: "Milk sale not found." });
                } else {
                    throw new Error("Failed to fetch milk sale details.");
                }
            } catch (error) {
                console.error("Error fetching sale details:", error);
                setNotification({ type: "danger", message: "An error occurred while fetching milk sale details." });
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchSaleDetails();
        }
    }, [id, token, navigate]);

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    if (loading) {
        return <p>Loading milk sale details...</p>;
    }

    if (!sale) {
        return (
            <main>
                <div className="column dash-section">
                    {notification.message && (
                        <Notification type={notification.type} message={notification.message} onClose={closeNotification} />
                    )}
                    <div className="has-text-centered">
                        <p className="title is-4">Milk sale not found</p>
                        <Link to="/dashboard/milk-sales" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back to Sales List
                        </Link>
                    </div>
                </div>
            </main>
        );
    }

    const aggregation = sale.route_aggregation;
    const seller = sale.seller;

    // Calculate remaining litres after this sale
    const aggregatedLitres = aggregation ? parseFloat(aggregation.litres) : 0;
    const soldLitres = parseFloat(sale.litres);
    const remainingLitres = aggregatedLitres - soldLitres;

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Milk Sale Details</p>
                    </div>
                </div>
                <div className="level-right">
                    <p className="level-item">
                        <Link to="/dashboard/milk-sales" className="button primary-bg">
                            <i className="fa fa-arrow-left" aria-hidden="true"></i> &nbsp; Back to List
                        </Link>
                    </p>
                </div>
            </nav>

            <div className="column dash-section">
                {notification.message && (
                    <Notification type={notification.type} message={notification.message} onClose={closeNotification} />
                )}

                <div className="columns">
                    {/* Left Column - Sale Details */}
                    <div className="column is-half">
                        <div className="card">
                            <header className="card-header">
                                <p className="card-header-title">
                                    <i className="fas fa-hand-holding-usd"></i>&nbsp; Sale Details
                                </p>
                            </header>
                            <div className="card-content">
                                <div className="content">
                                    <div className="field">
                                        <label className="label">Litres Sold</label>
                                        <p className="has-text-weight-bold is-size-5">{soldLitres.toFixed(2)} L</p>
                                    </div>
                                    <div className="field">
                                        <label className="label">Price per Litre</label>
                                        <p>{sale.price_per_litre ? formatCurrency(sale.price_per_litre) : "N/A"}</p>
                                    </div>
                                    <div className="field">
                                        <label className="label">Total Amount</label>
                                        <p className="">
                                            {sale.total_amount ? formatCurrency(sale.total_amount) : "N/A"}
                                        </p>
                                    </div>
                                    <div className="field">
                                        <label className="label">Remaining for Processor</label>
                                        <p className={`has-text-weight-bold ${remainingLitres < 0 ? "has-text-danger" : ""}`}>
                                            {remainingLitres.toFixed(2)} L
                                            {remainingLitres < 0 && " (Exceeds aggregated litres!)"}
                                        </p>
                                    </div>
                                    <div className="field">
                                        <label className="label">Sold By</label>
                                        <p>{seller?.name || "Unknown"}</p>
                                    </div>
                                    <div className="field">
                                        <label className="label">Notes</label>
                                        <p>{sale.notes || "No notes provided."}</p>
                                    </div>
                                    <div className="field">
                                        <label className="label">Sale Date</label>
                                        <p>{formatTimestamp(sale.created_at)}</p>
                                    </div>
                                    {sale.updated_at !== sale.created_at && (
                                        <div className="field">
                                            <label className="label">Last Updated</label>
                                            <p>{formatTimestamp(sale.updated_at)}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                           
                        </div>
                    </div>
                   
                    {/* Right Column - Aggregation Details */}
                    <div className="column is-half">
                        <div className="card">
                            <header className="card-header">
                                <p className="card-header-title">
                                    <i className="fas fa-truck"></i>&nbsp; Aggregation Details
                                </p>
                            </header>
                            <div className="card-content">
                                {aggregation ? (
                                    <div className="content">
                                        <div className="field">
                                            <label className="label">Route</label>
                                            <p>{aggregation.route?.name || "N/A"}</p>
                                        </div>
                                        <div className="field">
                                            <label className="label">Collection Date</label>
                                            <p>{formatToDayMonthYear(aggregation.collection_date)}</p>
                                        </div>
                                        <div className="field">
                                            <label className="label">Shift</label>
                                            <p>
                                                <span className={`tag ${aggregation.shift === "Morning" ? "is-info" : "is-warning"}`}>
                                                    {aggregation.shift}
                                                </span>
                                            </p>
                                        </div>
                                        <div className="field">
                                            <label className="label">Aggregated Litres</label>
                                            <p>{aggregatedLitres.toFixed(2)} L</p>
                                        </div>
                                        <div className="field">
                                            <label className="label">Aggregator</label>
                                            <p>{aggregation.aggregator?.name || "N/A"}</p>
                                        </div>
                                        <div className="field">
                                            <label className="label">Created At</label>
                                            <p>{formatTimestamp(aggregation.created_at) || "N/A"}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="has-text-danger">No aggregation data available.</p>
                                )}
                            </div>
                        </div>
                    </div>

                   
                </div>
            </div>
        </main>
    );
}

export default MilkSaleDetails;